import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useProducts, useCreateProduct, useUpdateProduct } from '../../hooks/useProducts'
import {
  useProductHardware,
  useReplaceProductHardware,
} from '../../hooks/useProductHardware'
import {
  useProductPackaging,
  useReplaceProductPackaging,
} from '../../hooks/useProductPackaging'
import {
  useProductPhotos,
  useUploadProductPhoto,
} from '../../hooks/useProductPhotos'
import { signedUrl } from '../../lib/storage'
import { BackLink } from '../../components/BackLink'

interface LineItemDraft {
  name: string
  quantity: string
  unitCost: string
}

export default function ProductForm() {
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { data: products } = useProducts()
  const createMutation = useCreateProduct()
  const updateMutation = useUpdateProduct()
  const existing = products?.find((p) => p.id === id)

  // Hardware / packaging / photos — only active when editing (need product id)
  const { data: existingHardware } = useProductHardware(id)
  const { data: existingPackaging } = useProductPackaging(id)
  const { data: existingPhotos } = useProductPhotos(id)
  const replaceHardware = useReplaceProductHardware(id ?? '')
  const replacePackaging = useReplaceProductPackaging(id ?? '')
  const uploadPhoto = useUploadProductPhoto(id ?? '')

  const initial = existing
    ? {
        name: existing.name,
        defaultUnitPrice:
          existing.default_unit_price != null ? String(existing.default_unit_price) : '',
        modelSource: existing.model_source ?? '',
        license: existing.license ?? 'Unknown',
        typicalPrintTimeHours:
          existing.typical_print_time_hours != null
            ? String(existing.typical_print_time_hours)
            : '',
        typicalFilamentGrams:
          existing.typical_filament_grams != null
            ? String(existing.typical_filament_grams)
            : '',
        typicalLaborMinutes: String(existing.typical_labor_minutes),
        defaultPostageCost: String(existing.default_postage_cost),
        momsCutPctOverride:
          existing.moms_cut_pct_override != null
            ? String(existing.moms_cut_pct_override)
            : '',
        notes: existing.notes ?? '',
      }
    : {
        name: '',
        defaultUnitPrice: '',
        modelSource: '',
        license: 'Unknown',
        typicalPrintTimeHours: '',
        typicalFilamentGrams: '',
        typicalLaborMinutes: '0',
        defaultPostageCost: '0',
        momsCutPctOverride: '',
        notes: '',
      }

  const [draft, setDraft] = useState(initial)
  const [errorMessage, setErrorMessage] = useState('')
  const [hardware, setHardware] = useState<LineItemDraft[]>([])
  const [packaging, setPackaging] = useState<LineItemDraft[]>([])
  const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null)
  const [photoUploadError, setPhotoUploadError] = useState('')

  // Sync hardware state once server data loads (runs once when data arrives).
  useEffect(() => {
    if (existingHardware) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHardware(
        existingHardware.map((h) => ({
          name: h.name,
          quantity: String(h.quantity),
          unitCost: String(h.unit_cost),
        })),
      )
    }
  }, [existingHardware])

  // Sync packaging state once server data loads.
  useEffect(() => {
    if (existingPackaging) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPackaging(
        existingPackaging.map((p) => ({
          name: p.name,
          quantity: String(p.quantity),
          unitCost: String(p.unit_cost),
        })),
      )
    }
  }, [existingPackaging])

  // Resolve signed URL for the cover photo.
  useEffect(() => {
    const cover = existingPhotos?.find((p) => p.is_cover) ?? existingPhotos?.[0]
    if (!cover) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCoverPhotoUrl(null)
      return
    }
    let cancelled = false
    signedUrl('photos', cover.file_path)
      .then((url) => {
        if (!cancelled) setCoverPhotoUrl(url)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [existingPhotos])

  // ── Row helpers ────────────────────────────────────────────────────────────

  const addHardware = () =>
    setHardware([...hardware, { name: '', quantity: '1', unitCost: '' }])
  const removeHardware = (idx: number) =>
    setHardware(hardware.filter((_, i) => i !== idx))
  const updateHardware = (idx: number, key: keyof LineItemDraft, val: string) =>
    setHardware(hardware.map((h, i) => (i === idx ? { ...h, [key]: val } : h)))

  const addPackaging = () =>
    setPackaging([...packaging, { name: '', quantity: '1', unitCost: '' }])
  const removePackaging = (idx: number) =>
    setPackaging(packaging.filter((_, i) => i !== idx))
  const updatePackaging = (idx: number, key: keyof LineItemDraft, val: string) =>
    setPackaging(packaging.map((p, i) => (i === idx ? { ...p, [key]: val } : p)))

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !id) return
    setPhotoUploadError('')
    try {
      await uploadPhoto.mutateAsync({ file, isCover: true })
    } catch (err) {
      setPhotoUploadError((err as Error).message)
    }
    e.target.value = ''
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    const payload = {
      name: draft.name.trim(),
      default_unit_price: draft.defaultUnitPrice ? Number(draft.defaultUnitPrice) : null,
      model_source: draft.modelSource || null,
      license: draft.license || null,
      typical_print_time_hours: draft.typicalPrintTimeHours
        ? Number(draft.typicalPrintTimeHours)
        : null,
      typical_filament_grams: draft.typicalFilamentGrams
        ? Number(draft.typicalFilamentGrams)
        : null,
      typical_labor_minutes: Number(draft.typicalLaborMinutes),
      default_postage_cost: Number(draft.defaultPostageCost),
      moms_cut_pct_override: draft.momsCutPctOverride
        ? Number(draft.momsCutPctOverride)
        : null,
      notes: draft.notes || null,
    }
    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, ...payload })
        await replaceHardware.mutateAsync(
          hardware
            .filter((h) => h.name.trim() && Number(h.quantity) > 0)
            .map((h) => ({
              name: h.name.trim(),
              quantity: Number(h.quantity),
              unit_cost: Number(h.unitCost || 0),
            })),
        )
        await replacePackaging.mutateAsync(
          packaging
            .filter((p) => p.name.trim() && Number(p.quantity) > 0)
            .map((p) => ({
              name: p.name.trim(),
              quantity: Number(p.quantity),
              unit_cost: Number(p.unitCost || 0),
            })),
        )
      } else {
        await createMutation.mutateAsync(payload)
      }
      navigate('/prints/products')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const set =
    (key: keyof typeof draft) =>
    (v: string) =>
      setDraft({ ...draft, [key]: v })

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    replaceHardware.isPending ||
    replacePackaging.isPending

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/prints/products" />
      <h1>{isEdit ? 'Edit product' : 'Add product'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        {/* ── Basic fields ── */}
        <Field
          label="Name"
          value={draft.name}
          onChange={set('name')}
          required
          hint="e.g., Articulated Dragon"
        />
        <Field
          label="Default unit price ($)"
          value={draft.defaultUnitPrice}
          onChange={set('defaultUnitPrice')}
          type="decimal"
          hint="Your usual asking price"
        />
        <Field
          label="Model source"
          value={draft.modelSource}
          onChange={set('modelSource')}
          hint="e.g., Printables: jklasdkj or self-designed"
        />
        <Field
          label="License"
          value={draft.license}
          onChange={set('license')}
          select={['Commercial OK', 'Personal only', 'Unknown']}
        />

        <h3
          style={{
            marginTop: 'var(--space-6)',
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--font-size-base)',
          }}
        >
          Typical values (optional)
        </h3>
        <Field
          label="Typical print time (hours)"
          value={draft.typicalPrintTimeHours}
          onChange={set('typicalPrintTimeHours')}
          type="decimal"
        />
        <Field
          label="Typical filament (grams)"
          value={draft.typicalFilamentGrams}
          onChange={set('typicalFilamentGrams')}
          type="decimal"
        />
        <Field
          label="Typical labor (minutes)"
          value={draft.typicalLaborMinutes}
          onChange={set('typicalLaborMinutes')}
          type="decimal"
        />
        <Field
          label="Default postage ($)"
          value={draft.defaultPostageCost}
          onChange={set('defaultPostageCost')}
          type="decimal"
        />
        <Field
          label="Mom's cut % override (optional)"
          value={draft.momsCutPctOverride}
          onChange={set('momsCutPctOverride')}
          type="decimal"
          hint="Leave blank to use the default from Settings. Set a number to override for this product only."
        />
        <Field label="Notes" value={draft.notes} onChange={set('notes')} multiline />

        {/* ── Edit-only sections ── */}
        {!isEdit ? (
          <div
            style={{
              background: 'var(--color-surface-2)',
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              marginTop: 'var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}
          >
            Save this product first, then come back to add hardware, packaging, and a cover
            photo.
          </div>
        ) : (
          <>
            {/* ── Cover photo ── */}
            <h3
              style={{
                marginTop: 'var(--space-6)',
                marginBottom: 'var(--space-3)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              Cover photo
            </h3>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              {coverPhotoUrl ? (
                <img
                  src={coverPhotoUrl}
                  alt="Cover"
                  style={{
                    width: '100%',
                    maxWidth: 240,
                    aspectRatio: '1/1',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-2)',
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 240,
                    aspectRatio: '1/1',
                    background: 'var(--color-surface-2)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-faint)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  No cover photo
                </div>
              )}
              <label
                style={{
                  display: 'inline-block',
                  padding: 'var(--space-2) var(--space-4)',
                  background: 'var(--color-primary)',
                  color: 'white',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-sm)',
                  cursor: uploadPhoto.isPending ? 'not-allowed' : 'pointer',
                  opacity: uploadPhoto.isPending ? 0.7 : 1,
                }}
              >
                {uploadPhoto.isPending
                  ? 'Uploading…'
                  : coverPhotoUrl
                  ? 'Replace cover'
                  : 'Upload cover photo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploadPhoto.isPending}
                  style={{ display: 'none' }}
                />
              </label>
              {photoUploadError && (
                <p
                  style={{
                    color: 'var(--color-danger)',
                    fontSize: 'var(--font-size-sm)',
                    marginTop: 'var(--space-2)',
                  }}
                >
                  {photoUploadError}
                </p>
              )}
            </div>

            {/* ── Hardware items ── */}
            <h3
              style={{
                marginTop: 'var(--space-6)',
                marginBottom: 'var(--space-3)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              Hardware items (optional)
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--space-3)',
              }}
            >
              Magnets, screws, eyes — anything you add to the print.
            </p>
            {hardware.map((h, idx) => (
              <div
                key={idx}
                style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
              >
                <input
                  type="text"
                  placeholder="Name (e.g., 'Magnet')"
                  value={h.name}
                  onChange={(e) => updateHardware(idx, 'name', e.target.value)}
                  style={{
                    flex: 2,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="qty"
                  value={h.quantity}
                  onChange={(e) => updateHardware(idx, 'quantity', e.target.value)}
                  style={{
                    width: 60,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="$ each"
                  value={h.unitCost}
                  onChange={(e) => updateHardware(idx, 'unitCost', e.target.value)}
                  style={{
                    width: 80,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeHardware(idx)}
                  style={{
                    color: 'var(--color-danger)',
                    padding: '0 var(--space-2)',
                    fontSize: 'var(--font-size-lg)',
                    lineHeight: 1,
                  }}
                  aria-label="Remove hardware item"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addHardware}
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--space-4)',
              }}
            >
              + Add hardware item
            </button>

            {/* ── Packaging items ── */}
            <h3
              style={{
                marginTop: 'var(--space-6)',
                marginBottom: 'var(--space-3)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              Packaging items (optional)
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--space-3)',
              }}
            >
              Bubble mailers, labels, thank-you cards.
            </p>
            {packaging.map((p, idx) => (
              <div
                key={idx}
                style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
              >
                <input
                  type="text"
                  placeholder="Name (e.g., 'Bubble mailer')"
                  value={p.name}
                  onChange={(e) => updatePackaging(idx, 'name', e.target.value)}
                  style={{
                    flex: 2,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="qty"
                  value={p.quantity}
                  onChange={(e) => updatePackaging(idx, 'quantity', e.target.value)}
                  style={{
                    width: 60,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="$ each"
                  value={p.unitCost}
                  onChange={(e) => updatePackaging(idx, 'unitCost', e.target.value)}
                  style={{
                    width: 80,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => removePackaging(idx)}
                  style={{
                    color: 'var(--color-danger)',
                    padding: '0 var(--space-2)',
                    fontSize: 'var(--font-size-lg)',
                    lineHeight: 1,
                  }}
                  aria-label="Remove packaging item"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPackaging}
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--space-4)',
              }}
            >
              + Add packaging item
            </button>
          </>
        )}

        {/* ── Submit ── */}
        <button
          type="submit"
          disabled={isSaving}
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Add product'}
        </button>

        {errorMessage && (
          <p
            style={{
              marginTop: 'var(--space-3)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required,
  multiline,
  select,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
  multiline?: boolean
  select?: string[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 320,
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  const textareaStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label
        style={{
          display: 'block',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          marginBottom: 'var(--space-1)',
        }}
      >
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {select.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={textareaStyle}
        />
      ) : (
        <input
          type={type === 'date' ? 'date' : 'text'}
          inputMode={type === 'decimal' ? 'decimal' : undefined}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={commonStyle}
        />
      )}
      {hint && (
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-faint)',
            marginTop: 'var(--space-1)',
          }}
        >
          {hint}
        </p>
      )}
    </div>
  )
}
